import {
  useGlobalObjectsSearchInfinite,
  useGlobalSearchInfinite
} from "@certa/queries/hooks/globalSearch.hook";
import { useDebouncedValue } from "../../../hooks/useDebounce";
import {
  WORKFLOW_CATEGORIES_WITH_ADVANCED_FILTERS,
  DATA_OBJECT_CATEGORIES_WITH_ADVANCED_FILTERS,
  MINIMUM_SEARCH_QUERY_LENGTH,
  SEARCH_CATEGORIES,
  SEARCH_DEBOUNCE_DELAY,
  SEARCH_QUERY_STALE_TIME,
  SEARCH_RESULT_TYPES
} from "../GlobalSearchWithSDO.constants";
import { useSearchStore } from "../store/useSearchStore";
import { useMemo } from "react";
import { getWorkflowIdsToFetchFamily } from "../../GlobalSearch/utils";
import { useGetWorkflowFamily } from "@certa/queries/workflows/workflowFamily";
import { useKinds } from "@certa/queries/hooks/kinds.hooks";
import { useGetObjectTypeTranslations } from "@certa/queries/hooks/dataObjects.hooks";

export const useGlobalSearch = () => {
  const {
    query,
    selectedCategories,
    filters,
    advancedFilterConditions,
    selectedKindIdForAdvancedFilters,
    areAdvancedFiltersApplied,
    selectedDataObjectTagForAdvancedFilters,
    dataObjectAdvancedFilters
  } = useSearchStore();
  const { isLoading: isLoadingKindsData } = useKinds();

  const { isLoading: isLoadingDataObjects } = useGetObjectTypeTranslations({
    config: {
      enabled: true
    }
  });

  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_DELAY);

  // Prepare category filters
  const selectedFileCategories = selectedCategories.includes(
    SEARCH_CATEGORIES.FILES
  )
    ? [SEARCH_CATEGORIES.FILES]
    : [];

  const selectedObjectCategories = selectedCategories.filter(
    category =>
      category !== SEARCH_CATEGORIES.WORKFLOWS &&
      category !== SEARCH_CATEGORIES.FILES
  );

  const selectedWorkflowCategories = selectedCategories.filter(
    category =>
      category !== SEARCH_CATEGORIES.DATA_RECORDS &&
      category !== SEARCH_CATEGORIES.FILES
  );

  const appliedCategories = areAdvancedFiltersApplied
    ? WORKFLOW_CATEGORIES_WITH_ADVANCED_FILTERS
    : selectedWorkflowCategories;

  const appliedObjectCategories = areAdvancedFiltersApplied
    ? DATA_OBJECT_CATEGORIES_WITH_ADVANCED_FILTERS
    : selectedObjectCategories;

  const hasWorkflowsInCategories = appliedCategories.includes(
    SEARCH_CATEGORIES.WORKFLOWS
  );

  const hasDataObjectsInCategories = appliedObjectCategories.includes(
    SEARCH_CATEGORIES.DATA_RECORDS
  );

  const kindIdsToFilterFrom = areAdvancedFiltersApplied
    ? [selectedKindIdForAdvancedFilters]
    : filters.kindIds;

  const objectTagsToFilterFrom = areAdvancedFiltersApplied
    ? [selectedDataObjectTagForAdvancedFilters]
    : filters.dataObjectTags;

  const kindIds = hasWorkflowsInCategories ? kindIdsToFilterFrom : [];
  const dataObjectTags = hasDataObjectsInCategories
    ? objectTagsToFilterFrom
    : [];

  // Common search config
  const isSearchEnabled = debouncedQuery.length > MINIMUM_SEARCH_QUERY_LENGTH;

  // Workflows API
  const workflowsQuery = useGlobalSearchInfinite({
    query: debouncedQuery,
    selectedCategories: appliedCategories,
    isSDOGlobalSearch: true,
    advancedFilterConditions,
    filters: { ...filters, kindIds },
    config: {
      enabled:
        isSearchEnabled &&
        !isLoadingKindsData &&
        appliedCategories.length > 0 &&
        hasWorkflowsInCategories,
      staleTime: SEARCH_QUERY_STALE_TIME
    }
  });

  // Files API
  const filesQuery = useGlobalSearchInfinite({
    query: debouncedQuery,
    selectedCategories: selectedFileCategories,
    isSDOGlobalSearch: true,
    advancedFilterConditions,
    filters: { ...filters, kindIds },
    config: {
      enabled: isSearchEnabled && selectedFileCategories.length > 0,
      staleTime: SEARCH_QUERY_STALE_TIME
    }
  });

  // Objects API
  const objectsQuery = useGlobalObjectsSearchInfinite({
    query: debouncedQuery,
    selectedCategories: appliedObjectCategories,
    advancedFilterConditions: dataObjectAdvancedFilters,
    filters: { ...filters, dataObjectTags },
    config: {
      enabled:
        isSearchEnabled &&
        !isLoadingDataObjects &&
        appliedObjectCategories.length > 0 &&
        hasDataObjectsInCategories,
      staleTime: SEARCH_QUERY_STALE_TIME
    }
  });

  // Process data from APIs
  const workflowsData = useMemo(() => {
    return workflowsQuery.data?.pages?.flatMap(page => page.results) ?? [];
  }, [workflowsQuery.data?.pages]);

  const filesData = useMemo(() => {
    return filesQuery.data?.pages?.flatMap(page => page.results) ?? [];
  }, [filesQuery.data]);

  const objectsData = useMemo(() => {
    return objectsQuery.data?.pages?.flatMap(page => page.results) ?? [];
  }, [objectsQuery.data]);

  // Extract workflow IDs using utility function
  const workflowIds = useMemo(
    () => getWorkflowIdsToFetchFamily([...workflowsData, ...filesData]),
    [workflowsData, filesData]
  );

  // Fetch workflow families using the hook
  const workflowFamilyData = useGetWorkflowFamily(workflowIds, {
    enabled: workflowIds.length > 0
  });

  const isAnyQueryLoading =
    workflowsQuery.isLoading || filesQuery.isLoading || objectsQuery.isLoading;

  return {
    [SEARCH_RESULT_TYPES.WORKFLOWS]: {
      ...workflowsQuery,
      flatData: workflowsData
    },
    [SEARCH_RESULT_TYPES.FILES]: {
      ...filesQuery,
      flatData: filesData
    },
    [SEARCH_RESULT_TYPES.DATA_RECORDS]: {
      ...objectsQuery,
      flatData: objectsData
    },
    workflowFamilyData,
    isAnyQueryLoading
  } as const;
};
